import React, {
	useMemo,
	useState,
	useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Socket, { ConnectedUser } from '../../../../connection/socket';
import { Invitation } from '../../../../stores/reducers/invitations';
import { Meeting, ShowSession, ShowSessionType } from '../../../../stores/reducers/session';
import { MODAL_TEMPLATES, MODAL_INFO_ACTIONS } from '../../../../stores/reducers/modal';
import { GreenroomState as GRS } from '../../../../stores/types/types';
import * as InviteActions from '../../../../stores/actions/invitations';
import * as ModalActions from '../../../../stores/actions/modal';
import Colors from '../../../../utils/colors';
import MeetingRow from '../../meeting-row';
import Icon, { ICONS, COLORS } from '../../../general-components/icon';
import Avatar from '../../../general-components/avatar';
import LoadingSpinner from '../../../general-components/loading-spinner';
import * as Alerts from '../../../general-components/alert-service';
import useShots from '../../../../contexts/shots/provider';
import ScheduledStreamCard from '../../../broadcasting/settings/scheduled-stream-card';
import {
	LiveStreamConfirmationModalProps,
	LiveStreamStart,
} from '../../../modal/templates/live-stream-confirmation';
import { FEED_TYPES } from '../../../../stores/reducers/shots';
import { CUSTOM_DESTINATION_OPTIONS } from '../../../../stores/types/broadcasting';

interface Props {
	connected: boolean;
	homeMeeting: any;
	invitations: Invitation[];
	showSessions: ShowSession[];
	showSessionTypes: ShowSessionType[];
	joinGreenroom: (meeting: Meeting | null, fromRow?: boolean) => Promise<boolean>;
	buttonDisabled?: boolean;
	socket: Socket | null;
}

export default function ShowDetailsInfo(props: Props) {
	const host = useSelector((s: GRS) => s.SessionReducer.host);
	const user = useSelector((s: GRS) => s.SessionReducer.user);
	const token = useSelector((s: GRS) => s.SessionReducer.token);
	const showSessionsLoading = useSelector((s: GRS) => s.SessionReducer.showSessionsLoading);
	const connectedUsers = useSelector((s: GRS) => s.SessionReducer.connectedUsers);
	const liveChannels = useSelector((s: GRS) => s.ContentReducer.liveChannels);
	const invitationsLoading = useSelector((s: GRS) => s.InvitationsReducer.invitationsLoading);
	const shots = useSelector((s: GRS) => s.ShotsReducer.shots);
	const scheduledStream = useSelector((s: GRS) => s.MeetingReducer.scheduledStream);
	const destinations = useSelector((s: GRS) => s.BroadcastReducer.destinations);
	const dispatch = useDispatch();
	const {
		connected,
		homeMeeting,
		invitations,
		showSessions,
		showSessionTypes,
		joinGreenroom,
		buttonDisabled = false,
		socket,
	} = props;

	const [presenterShowAll, setPresenterShowAll] = useState(false);
	const [presenterCount, setPresenterCount] = useState(0);
	const { stripFeedFromShots } = useShots();

	const revokePresenter = useCallback(
		(invitation: Invitation): void => {
			dispatch(
				InviteActions.revokeInvitation(
					token as string,
					invitation.profile.email,
					homeMeeting.channel,
					homeMeeting.meeting,
				),
			);

			socket?.sendMessage(
				'invites-update',
				invitations.filter(
					(invite) => invite.profile.email !== invitation.profile.email,
				),
			);

			if (showSessions.length > 0) {
				socket?.sendMessage('show-sessions-update', {
					token,
					meeting: homeMeeting.meeting,
				});
			}

			if (invitation.profile?.gr_user) {
				stripFeedFromShots(
					invitation.profile.gr_user,
					FEED_TYPES.PRESENTER,
					false,
					invitation.profile.email,
				);
			}
		},
		[
			homeMeeting.channel,
			homeMeeting.meeting,
			dispatch,
			invitations,
			socket,
			stripFeedFromShots,
			showSessions,
			token,
			shots,
		],
	);

	// eslint-disable-next-line consistent-return
	function invitePresenterModal() {
		if (!homeMeeting.meeting) {
			return Alerts.showAlert({
				message: 'Set up',
				description: 'Please set up the event before inviting a presenter',
				duration: 2000,
				type: 'neutral',
			});
		}

		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		openModal({ modalInfoAction: MODAL_INFO_ACTIONS.INVITE });
	}

	// eslint-disable-next-line consistent-return
	function addShowSessionModal() {
		if (!homeMeeting.meeting) {
			return Alerts.showAlert({
				message: 'Set up',
				description: 'Please set up the event before adding a rehearsal',
				duration: 2000,
				type: 'neutral',
			});
		}

		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		openModal({
			modalInfoAction: MODAL_INFO_ACTIONS.ADD,
			showSessionTypes,
		});
	}

	function modifyShowSessionModal(showSessionId: number, type: string) {
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		const selectedShowSession = getEvents().find((event: any) => event.id === showSessionId);

		if (selectedShowSession) {
			const selectedShowSessionDetails: ShowSession = {
				meeting: selectedShowSession.meeting,
				session_type_id: selectedShowSession.session_type_id,
				start_time: selectedShowSession.start_time,
				end_time: selectedShowSession.end_time,
				id: showSessionId,
				title: selectedShowSession.title,
				invitations: selectedShowSession.invites.map(
					(invitation: Invitation) => invitation.profile.email,
				),
			};

			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			openModal({
				modalInfoAction: type,
				showSession: selectedShowSessionDetails,
				showSessionTypes,
			});
		}
	}

	const openModal = (data: any) => {
		dispatch(ModalActions.setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.EVENT_SETTINGS,
			modalData: {
				...data,
				socket,
				showInvitations: invitations,
			},
		}));
	};

	const presenters = useMemo(() => {
		setPresenterCount(invitations.length);
		const presentersDisplay = presenterShowAll ? [...invitations] : invitations.slice(0, 6);
		return presentersDisplay.map((presenter: Invitation, index: number) => (
			<div className="presenter" key={index}>
				<Avatar noBorder={false} user={presenter.profile} />
				{connectedUsers.find((conUser: ConnectedUser) => conUser.gr_user === presenter.profile.gr_user && conUser?.in_room) && <span className="online" />}
				<div className="presenter-meta">
					{(presenter.profile.first_name || presenter.profile.last_name)
						? <label className="invite-user-name">{presenter.profile.first_name} {presenter.profile.last_name}</label>
						: <label className="invite-user-name">{presenter.profile.email}</label>
					}
					{(presenter.profile.title) && <span>{presenter.profile.title}</span>}
				</div>
				{host && (
					<button className="x-button" onClick={() => revokePresenter(presenter)}>
						<Icon name={ICONS.CLOSE} size={12} color={COLORS.WHITE} />
					</button>
				)}
			</div>
		));
	}, [homeMeeting, invitations, presenterShowAll, shots, revokePresenter, connectedUsers]);

	const getEvents = () => showSessions
		.map((showSession: any) => {
			let invites: Invitation[] = [];

			if (showSession.invitations?.length > 0) {
				invites = invitations.filter((invitation) => showSession.invitations
					.toString()
					.split(',')
					.some(
						(inviteEmail: string) => inviteEmail === invitation.profile.email,
					));
			}

			const newSessionDetails: any = {
				id: showSession.id,
				meeting: homeMeeting.meeting,
				broadcast_length: (
					new Date(showSession.end_time).valueOf()
					- new Date(showSession.start_time).valueOf()
				) / 60000,
				start_time: showSession.start_time,
				end_time: showSession.end_time,
				title: showSession.title ? showSession.title : homeMeeting.title,
				invites,
				session_type_id: showSession.session_type_id,
				show_session_type:
					!showSessionTypes || showSessionTypes.length === 0
						? 'Broadcast'
						: showSessionTypes.find(
							(showSessionType: ShowSessionType) =>
								// eslint-disable-next-line implicit-arrow-linebreak
								showSessionType.id === showSession.session_type_id,
						)?.name ?? 'Broadcast',
			};

			return newSessionDetails;
		}) // presenters can only see show sessions that they are invited to
		// eslint-disable-next-line no-confusing-arrow
		.filter((showSession: any) => host
			? true
			: showSession.invites.some(
				({ profile }: Invitation) => profile.email === user?.email,
			));

	const showSessionsList = useMemo(
		() => getEvents().map((showSession: any, index: number) => (
			<MeetingRow
				key={index}
				{...homeMeeting}
				{...{
					...showSession,
					invites: showSession.invites.map((invite: any) => invite.profile),
				}}
				showSessionId={showSession.id}
				show_session_type={
					showSessionTypes.find(
						(showSessionType) => showSessionType.id === showSession.session_type_id,
					)?.name
				}
				onClick={joinGreenroom}
				connected={connected}
				forPresenter={!host}
				buttonDisabled={buttonDisabled}
				isLive={
					liveChannels.includes(showSession.meeting)
					|| showSession.entry_without_producer
				}
				inShowDetailsView={true}
				canEdit={true}
				modifyEvent={modifyShowSessionModal}
			/>
		)),
		[
			showSessions,
			showSessionTypes,
			connected,
			host,
			buttonDisabled,
			liveChannels,
			invitations,
		],
	);

	const endpointName = useMemo(() => {
		const endpointData = destinations.filter((destination) => destination.selected)[0];
		return endpointData?.id ? endpointData.name : CUSTOM_DESTINATION_OPTIONS[0].label;
	}, [destinations]);

	return (
		<>
			<div className="show-details-info">
				{invitationsLoading && showSessionsLoading ? (
					<div className="loading-container">
						<LoadingSpinner height={100} width={100} color={Colors.red} />
					</div>
				) : (
					<>
						<div className="section-header">
							<label>Presenters</label>
							{host && (
								<div className="section-buttons">
									<button
										onClick={invitePresenterModal}
										className="transparent"
									>
										<Icon
											name={ICONS.INVITE}
											color={COLORS.PALE_GREEN}
											size={14}
											className="margin-right-small"
										/>
										<span className="text-green">Invite</span>
									</button>
								</div>
							)}
						</div>
						{presenters.length > 0 ? (
							<div className="presenters-list">{presenters}</div>
						) : (
							<div className="no-results">
								<p>There are no presenters invited yet</p>
							</div>
						)}
						{presenterCount > 6 && (
							<div
								className="show-more"
								onClick={() => setPresenterShowAll(!presenterShowAll)}
							>
								<button>
									<span>{presenterShowAll ? 'Show less' : 'Show more'}</span>
									<Icon
										name={
											presenterShowAll
												? ICONS.KEYBOARD_ARROW_UP
												: ICONS.KEYBOARD_ARROW_DOWN
										}
										size={15}
										color={COLORS.PALE_GREEN}
									/>
								</button>
							</div>
						)}
						<div className="section-header rehearsals">
							<label>Schedule</label>
							{host && (
								<div className="section-buttons">
									<button onClick={addShowSessionModal} className="transparent">
										<Icon
											name={ICONS.ADD_EVENT}
											color={COLORS.PALE_GREEN}
											size={14}
											className="margin-right-small"
										/>
										<span className="text-green">Add</span>
									</button>
								</div>
							)}
						</div>
						{scheduledStream?.startTime && scheduledStream.startTime.valueOf() > Date.now() && (
							<div className="margin-bottom-small">
								<ScheduledStreamCard
									date={scheduledStream.startTime}
									location={endpointName}
									actionHandler={() => {
										dispatch(
											ModalActions.setModalState({
												modalOpen: true,
												modalTemplate:
													MODAL_TEMPLATES.LIVE_STREAM_CONFIRMATION,
												modalData: {
													socket,
													initialMode: LiveStreamStart.Schedule,
												} as LiveStreamConfirmationModalProps,
											}),
										);
									}}
								/>
							</div>
						)}
						<div className="rehearsals-list">
							{showSessionsList.length > 0 || homeMeeting ? (
								<div className="rehearsals-list">
									{showSessionsList}
									{homeMeeting && (
										<MeetingRow
											{...homeMeeting}
											invites={invitations.map(
												(invite: Invitation) => invite.profile,
											)}
											show_session_type={'Broadcast'}
											onClick={joinGreenroom}
											connected={connected}
											forPresenter={!host}
											buttonDisabled={buttonDisabled}
											inShowDetailsView={true}
										/>
									)}
								</div>
							) : (
								<div className="no-results">
									<p>No events scheduled</p>
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</>
	);
}
